import axios from 'axios';
import { CognitoRefreshToken, CognitoUserPool } from 'amazon-cognito-identity-js';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment';

const TOKEN_GRACE_PERIOD = 60; // Refresh token 60 seconds before expiry
let refreshingToken = false;
let requestQueue = [];

const userPool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
});

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

export const setAuthToken = (token: string) => {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
};

export const validateWebsocketToken = async (token) => {
  try {
    const decoded = jwtDecode(token);
    const currentTimestamp = moment().unix();
    const exp = decoded?.exp;
    const bufferExpiry = exp - TOKEN_GRACE_PERIOD;
    const refreshToken = localStorage.getItem('refreshToken');
    const cognitoUser = userPool.getCurrentUser();

    if (currentTimestamp >= bufferExpiry) {
      //@ts-ignore
      const { token: newToken, session } = await handleSessionRefresh(cognitoUser, refreshToken);

      if (!newToken) {
        console.error('Authentication failed: Unable to refresh session.');
        handleLogout();
        return null;
      }

      return newToken;
    } else {
      return token;
    }
  } catch (error) {
    console.error('Error validating websocket token:', error);
    handleLogout();
    return null;
  }
};

export const handleLogout = () => {
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('accessToken');
  refreshingToken = false;
  requestQueue = [];
  window.location.href = '/';
};

const processRequestQueue = (token) => {
  while (requestQueue.length > 0) {
    const { request, resolve } = requestQueue.shift();

    const newRequest = {
      ...request,
      headers: {
        ...request.headers,
        Authorization: `Bearer ${token}`,
      },
    };

    resolve(newRequest);
  }

  refreshingToken = false;
};

const handleSessionRefresh = async (cognitoUser, refreshToken) => {
  return new Promise((resolve, reject) => {
    // First check if current session is valid
    cognitoUser.getSession((sessionErr, currentSession) => {
      const fetchedToken = new CognitoRefreshToken({
        RefreshToken: refreshToken,
      });
      cognitoUser.refreshSession(fetchedToken, async (err, session) => {
        if (err) {
          reject(err);
          return;
        }
        const newToken = session?.getAccessToken()?.getJwtToken();
        const newRefreshToken = session?.getRefreshToken()?.getToken();

        if (!newToken || !newRefreshToken) {
          reject(new Error('Invalid session tokens'));
          return;
        }

        localStorage.setItem('refreshToken', newRefreshToken);
        resolve({ token: newToken, session });
      });
    });
  });
};

const checkTokenAndQueue = async (request) => {
  if (request.headers && request.headers['X-Skip-Auth']) {
    return request;
  }

  try {
    const token = request?.headers?.Authorization?.split(' ')[1];
    const refreshToken = localStorage.getItem('refreshToken');

    if (window.location.pathname.includes('/register')) {
      return request;
    }

    if (!token || !refreshToken) {
      handleLogout();
      return Promise.reject(new Error('Missing authentication tokens'));
    }

    const decoded = jwtDecode(token);
    const currentTimestamp = moment().unix();
    const exp = decoded?.exp;
    const bufferExpiry = exp - TOKEN_GRACE_PERIOD;

    if (currentTimestamp >= bufferExpiry) {
      if (refreshingToken) {
        return new Promise((resolve, reject) => {
          requestQueue.push({ request, resolve, reject });
        });
      }

      refreshingToken = true;

      return new Promise(async (resolve, reject) => {
        const cognitoUser = userPool.getCurrentUser();
        if (!cognitoUser) {
          refreshingToken = false;
          handleLogout();
          reject('No user found');
          return;
        }

        try {
          //@ts-ignore
          const { token: newToken, session } = await handleSessionRefresh(
            cognitoUser,
            refreshToken
          );

          setAuthToken(newToken);
          requestQueue.push({ request, resolve, reject });
          processRequestQueue(newToken);
        } catch (error) {
          console.error('ERROR IN REFRESHING TOKEN', error);
          refreshingToken = false;
          handleLogout();
          reject(error);
        }
      });
    }
    return request;
  } catch (error) {
    console.error('Error checking token:', error);
    handleLogout();
    return Promise.reject(error);
  }
};

// Request interceptor - adds auth header and handles token refresh
api.interceptors.request.use(
  async (config) => {
    const isImpersonating = localStorage.getItem('isImpersonating') === 'true';

    if (isImpersonating) {
      const accessToken = localStorage.getItem('accessToken');

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    }

    return await checkTokenAndQueue(config);
  },
  (error) => Promise.reject(error)
);

export default api;
